/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 重要課題の一覧取得・作成・更新・削除・並び替えの React Query フック群。
 * @graph-connects keyIssueApi [calls] 重要課題 API クライアント
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keyIssueApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { KeyIssueInput } from '../types/key-issue.js';

/** @graph-connects none */
export function useKeyIssues() {
  return useQuery({
    queryKey: queryKeys.keyIssues.list,
    queryFn: keyIssueApi.list,
  });
}

/** @graph-connects none */
export function useCreateKeyIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: KeyIssueInput) => keyIssueApi.create(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.keyIssues.list }),
  });
}

/** @graph-connects none */
export function useUpdateKeyIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: KeyIssueInput }) =>
      keyIssueApi.update(id, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.keyIssues.list }),
  });
}

/** @graph-connects none */
export function useDeleteKeyIssue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => keyIssueApi.remove(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.keyIssues.list }),
  });
}

/** @graph-connects none */
export function useReorderKeyIssues() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: string[]) => keyIssueApi.reorder(orderedIds),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.keyIssues.list }),
  });
}
